import React, {useEffect, useState} from "react";
import {CRITERIA_MODEL, DEFAULT_LEFT, SEARCH_MODEL} from "../../../../../components/table/Model/TableModel";
import {SearchByFilters, SimpleSearchWithId} from "../../../../../reduxToolkit/Search/SearchApi";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {TABLES} from "../../../../../Apis/Tables";
import CustomTh from "../../../../../components/table/CustomTh";
import CustomTd from "../../../../../components/table/CustomTd";
import CustomChip from "../../../../../components/CustomChip";
import {status, STATUS_MODEL} from "../../../../../enums/statusEnum";
import {statusBorderColor, statusColor, statusFontColor} from "../../../../../enums/statusColorEnum";
import {colors} from "../../../../../enums/colors";
import DropDownMenuProfile from "../../../../../components/DropDown/DropDownMenuProfile";
import {permissionObj} from "../../../../../Helpers/permission";
import DropDownMenuItem from "../../../../../components/DropDown/DropDownMenuItem";
import {
    CancelIconsOptions,
    DetailsIconsOptions,
    IconAvatarTable,
    IconClockTable,
    IconDeskOfficeTable,
    IconMarkerTable,
    IconScheduleTable,
    LinkIconsOptions
} from "../../../../../components/buttons/IconButtons";
import TableFunction from "../../../../../components/table/TableFunction";
import {EMPTY_VALUES, getDateFormat, getFullName, getTimeFormat} from "../../../../../Apis/functions";
import {CancelInvitation, CountAllByAllStatus} from "../../../../../reduxToolkit/Invitation/InvitationApi";
import {toast} from "react-toastify";
import CustomImageTable from "../../../../../components/CustomImageTable";
import {DATA_TYPE_MODEL, ORDER_MODEL, SEARCH_OPERATION_MODEL} from "../../../../Modals/Model/Model";
import {INVITATION_VIEW_TYPE, invitationInTime} from "../Model/Model";
import {fieldModelName} from "../../../../../enums/fieldModelEnum";
import CustomTooltip from "../../../../../components/CustomTooltip";

/**
 * @author Karen Yunuen Pozos Arias
 * @email kpozos@ibl.mx
 * @title Full Stack Developer
 *
 * OngoingTableApp component manages the logic for the "Ongoing Visitor Invitation" table.
 * It handles rendering, filtering, pagination, and managing invitation status changes.
 * The component uses Redux for fetching data and manages internal state for UI control.
 *
 * @component
 * @param {Array<string>} selectedList - List of selected row IDs (required).
 * @param {Function} setSelectedList - Function to update selected row IDs (required).
 * @param {Object} criteria - Current search criteria object (required).
 * @param {Function} setCriteria - Setter for the search criteria object (required).
 * @param {string} searchElement - The current search text input (required).
 * @param {Array<Object>} finalArray - Final array of search filters (used to generate criteria) (required).
 * @param {Function} setSearch - Setter to trigger a new search (required).
 *
 * @param setBodyForExport - Sets the data body for export functionality (required)
 * @param {boolean} modified - Flag indicating that data has changed and the table should refresh (required).
 * @param {Function} setModified - Setter used to update the `modified` flag and trigger data reloads (required).
 * @returns {Object} Object containing all necessary table handlers, renderers, and state values:
 * - `thead`, `tbody`: Rendered table header and body.
 * - `pagination`: JSX pagination controls.
 * - `confirmModal`, `detailsModal`: Modal visibility booleans.
 * - `confirmFn`: Function to confirm invitation status changes.
 * - `getActionsButtons`: Function to render row-specific dropdown actions.
 * - and other helpers like `handleCheckboxChange`, `handleDeleteAll`, etc.
 *
 */


export const  OngoingTableApp = ({
    selectedList,
    setSelectedList,
    criteria,
    setCriteria,
    searchElement,
    finalArray,
    setSearch,
    setBodyForExport,
    modified,
    setModified
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const {permission} = useSelector((state) => state.authenticatioauthennSlice);
    const userId = useSelector((state) => state.authenticatioauthennSlice.getUserByIdAtEntry.userId);

    const [isAllChecked, setIsAllChecked] = useState(false);
    const [simpleSearch, setSimpleSearch] = useState(false); // Simple search flag
    const tableId = "tbl_cta_visitor_invitation"; // Set the table ID for identification
    const [results, setResults] = useState(null); // Store the search results
    const [cancelModal, setCancelModal] = useState(false);
    const [detailsModal, setDetailsModal] = useState(false);
    const [invitationLinkModal, setInvitationLinkModal] = useState(false);
    const [itemSelected, setItemSelected] = useState(null);
    const searchId = 'txt_af_search_ongoing_invitation';
    const option = TABLES.USER_INVITATION;
    const leftPosition = DEFAULT_LEFT;

    useEffect(() => {
        dispatch(CountAllByAllStatus(userId));
    }, []);

    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes="first_head">{t('visitor_name')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('visitor_number')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('type')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('access_zone')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('start_date')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('end_date')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('approval_status')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('status')}</CustomTh>,
        ];
    };

    const getTbody = () => {
        if (!results || results?.content?.length === 0) return {
            body: [],
            data: []
        };

        const body = results?.content?.map((item) => {
            const fullName = getFullName(item?.visitorName, item?.visitorLastName, item?.visitorSecondLastName);
            const visitorStatusId = item?.visitorStatusId;
            const approvalStatusId = item?.approvalStatusId;
            const statusId = item?.statusId;

            return (
                <>
                    <CustomTd tableId={tableId} fixed={true} left={leftPosition} classes={"first-column"}>
                        <CustomImageTable image={IconAvatarTable} containerClass={"d-flex align-items-center"}>
                            <div className={"ml-1"}>
                                <p className={"mb-1"}>{fullName}</p>
                                <CustomChip legend={t(status[visitorStatusId])}
                                            borderColor={statusBorderColor[visitorStatusId]}
                                            fontColor={statusFontColor[visitorStatusId]}
                                            background={statusColor[visitorStatusId]}
                                            variant={'large'}
                                />
                            </div>
                        </CustomImageTable>
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        {item?.visitorNumber}
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomChip
                            background={colors.secondary100}
                            fontColor={colors.secondary700}
                            borderColor={colors.secondary150}
                            legend={item?.visitorType?.name || EMPTY_VALUES.DEFAULT}
                            variant={'compact'}
                        />
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        {item?.rootFatherZoneName &&
                        <div className={"d-flex align-items-center"}>
                            <div>
                                <IconMarkerTable className={"mr-8px mb-1"}/>
                            </div>
                            <div className={"flex-grow-1 minw-0"}>
                                <CustomTooltip
                                    text={item?.rootFatherZoneName}
                                    classes={"font-color-shades-500 fs-14px text-ellipsis-1-row"}
                                    showIfTruncated={true}
                                    placement={"top"}
                                />
                            </div>
                        </div>
                        }

                        <div className={"d-flex align-items-center"}>
                            {item?.rootFatherZoneName ? <div><IconDeskOfficeTable className={"mr-8px mb-1"}/></div>
                                : <div><IconMarkerTable className={"mr-8px mb-1"}/></div>}

                            <div className={"flex-grow-1 minw-0"}>
                                <CustomTooltip
                                    text={item?.zoneName}
                                    classes={"font-color-shades-500 fs-14px text-ellipsis-1-row"}
                                    showIfTruncated={true}
                                    placement={"top"}
                                />
                            </div>
                        </div>
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <div className={"d-flex"}>
                            <div>
                                <IconScheduleTable className={"mr-8px mb-1 icon-color-general-950"}/>
                            </div>
                            {getDateFormat(item?.startDate, false)}
                        </div>
                        <div className={"d-flex"}>
                            <div>
                                <IconClockTable className={"mr-8px mb-1 icon-color-general-950"}/>
                            </div>
                            {getTimeFormat(item?.startDate)}
                        </div>
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <div className={"d-flex"}>
                            <div>
                                <IconScheduleTable className={"mr-8px mb-1 icon-color-general-950"}/>
                            </div>
                            {getDateFormat(item?.endDate, false)}
                        </div>
                        <div className={"d-flex"}>
                            <div>
                                <IconClockTable className={"mr-8px mb-1 icon-color-general-950"}/>
                            </div>
                            {getTimeFormat(item?.endDate)}
                        </div>
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomChip legend={t(status[approvalStatusId])}
                                    borderColor={statusBorderColor[approvalStatusId]}
                                    fontColor={statusFontColor[approvalStatusId]}
                                    background={statusColor[approvalStatusId]}
                                    variant={'large'}
                        />
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomChip legend={t(status[statusId])}
                                    borderColor={statusBorderColor[statusId]}
                                    fontColor={statusFontColor[statusId]}
                                    background={statusColor[statusId]}
                                    variant={'large'}
                        />
                    </CustomTd>
                </>
            );
        });

        return {
            body: body, // Return the generated table rows
            data: results?.content // Return the data associated with the rows
        };
    };

    const getActionsButtons = (item, index) => {
        const inTime = invitationInTime(item?.endDate);
        return <>
            <DropDownMenuProfile
                openMenu={openMenu}
                handleMenuToggle={handleMenuToggle}
                menuId={`menu-${item.id}`} // Unique menu ID for each contractor
            >
                <DropDownMenuItem
                    id={"btn_ota_details_" + item?.id}
                    index={index}
                    handleClose={handleClose}
                    name={t('details')}
                    click={() => {
                        setSelectedList([item?.id]);
                        setItemSelected(item);
                        setDetailsModal(true);
                    }}
                    icon={<DetailsIconsOptions/>}
                />
                {(item?.approvalStatusId === STATUS_MODEL.APPROVED)
                && item?.visitorStatusId !== STATUS_MODEL.CANCELED
                && item?.visitorStatusId !== STATUS_MODEL.REJECTED
                && inTime &&
                    <DropDownMenuItem
                        id={"btn_ota_link_" + item?.id}
                        index={index}
                        handleClose={handleClose}
                        name={t('invitation_link')}
                        click={() => {
                            setSelectedList([item?.id]);
                            setItemSelected(item);
                            setInvitationLinkModal(true);
                        }}
                        icon={<LinkIconsOptions/>}
                    />
                }

                { ((DEV || permission.includes(permissionObj?.WEB_VISITOR_INVITATION_MANAGE))
                    && item?.statusId !== STATUS_MODEL.CANCELED
                    && item?.approvalStatusId !== STATUS_MODEL.DENIED)
                    && item?.visitorStatusId !== STATUS_MODEL.CANCELED
                    && item?.visitorStatusId !== STATUS_MODEL.REJECTED
                    && inTime &&
                <DropDownMenuItem
                    id={"btn_ota_cancel_" + item?.id}
                    index={index}
                    handleClose={handleClose}
                    name={t('cancel')}
                    fontColor={"font-color-error-600"}
                    iconColor={"icon-color-error-code-600"}
                    click={() => {
                        setSelectedList([item?.id]); // Set selected item ID for deletion
                        setCancelModal(true);
                    }}
                    icon={<CancelIconsOptions/>}
                />
                }
            </DropDownMenuProfile>
        </>;
    };

    const cancelFn = (ids) => {
        dispatch(CancelInvitation({ids:ids})).then(
            ({
                 payload: {
                     data: {success},
                 },
             }) => {
                if (success === true) {
                    setModified(true);
                    setSelectedList([]);
                    setIsAllChecked(false);
                    toast.success(t("has_been_cancelled_successfully"));
                    dispatch(CountAllByAllStatus(userId));
                }
            },
        );
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({page, size:rowsPerPage, model:option, userId: userId, viewType: INVITATION_VIEW_TYPE.ONGOING, key:searchElement});
        setBodyForExport(body);
        dispatch(SimpleSearchWithId({ body })).then(
            ({payload: {data: { data, success }}}) => {
                if (success === true) {
                    setResults(data);
                }
            },
        );
    }

    /**
     * Executes a paginated search request using advanced criteria.
     *
     * Builds the criteria list based on the advanced search (finalArray),
     * appends user criteria, and dispatches the search action through Redux.
     * Updates the results and resets the selected list once the data is returned.
     */
    const getDataAdvancedSearch = (page) => {
        const criteriaList = finalArray.map((item) => {
            return CRITERIA_MODEL.set(item);
        });
        criteriaList.push(userCriteria());
        criteriaList.push(deletedCriteria());
        const body = SEARCH_MODEL.setObject({page, size:rowsPerPage, model:option, criteria:criteriaList});
        setCriteria(body);
        dispatch(SearchByFilters({ option, body })).then(({ payload: {data: { data }}}) => {
            setResults(data);
        });
    }

    // Use the TableFunction SubComponents to handle table logic (checkbox selection, pagination, etc.)
    const { thead,tbody, t, handleCheckboxChange, handleDeleteAll, pagination, page, rowsPerPage,
        openMenu, handleMenuToggle, handleClose} = TableFunction({
        getTbody: getTbody,
        getThead: getThead,
        selectedList,
        setSelectedList,
        data: results,
        labelRowsPerPage: 'invitation_per_page',
        hasContainer: true,
        fixedPagination: false,
        setIsAllChecked,

        searchElement, //New
        getData,//New
        modified,//New
        setModified,//New
        getDataAdvancedSearch,//New - apply only with advanced search
        finalArray,//New - apply only with advanced search
    });

    /**
     * Builds a filter criteria to search invitations where the current user is the host.
     * @returns {Object} Filter criteria with host ID and AND operator.
     */
    const userCriteria = () => {
        return CRITERIA_MODEL.set({
            "dataOption": SEARCH_OPERATION_MODEL.AND,
            "fieldType": DATA_TYPE_MODEL.UUID,
            "filterKey": fieldModelName.host,
            "operation": SEARCH_OPERATION_MODEL.EQ,
            "sort": ORDER_MODEL.ASC,
            "table": option,
            "values": [userId]
        });
    }

    /**
     * Builds a filter criteria to search invitations where isn´t deñeted
     * @returns {Object} Filter criteria with deleted and AND operator.
     */
    const deletedCriteria = () => {
        return CRITERIA_MODEL.set({
            "dataOption": SEARCH_OPERATION_MODEL.AND,
            "fieldType": DATA_TYPE_MODEL.BOOLEAN,
            "filterKey": fieldModelName.deleted,
            "operation": SEARCH_OPERATION_MODEL.EQ,
            "sort": ORDER_MODEL.ASC,
            "table": option,
            "values": [false]
        });
    }

    /**
     * Updates search criteria whenever the `searchElement` value changes.
     * If empty, filters only by user (visitor). Otherwise, includes additional OR filters
     * for fields like host and zone.
     * Triggers the search flag to refresh the table data.
     */
    useEffect(() => {
        if(searchElement === "")  setCriteria({...criteria, searchCriteriaList:[userCriteria(), deletedCriteria()]});
        else setCriteria({...criteria, searchCriteriaList:[userCriteria(), deletedCriteria(), ...exportFilters()]});
        setSearch(true);
    }, [searchElement]);

    /**
     * Constructs OR-based search filters for search fields.
     * Filters include visitor name, zone, and visitor type using the search text.
     * @returns {Object[]} List of filter criteria with OR logic.
     */
    const exportFilters = () => {
        let stringFields = [fieldModelName.visitor, fieldModelName.zone, fieldModelName.visitorType]
        let aux = [];
        stringFields.map(field => aux.push(CRITERIA_MODEL.set({
            "dataOption": SEARCH_OPERATION_MODEL.OR,
            "fieldType": DATA_TYPE_MODEL.STRING,
            "filterKey": field,
            "operation": SEARCH_OPERATION_MODEL.CN,
            "sort": ORDER_MODEL.ASC,
            "table": option,
            "values": [searchElement]
        })));
        return aux;
    }

    return {
        t,
        DEV,
        userId,
        permission,
        navigate,
        setResults,
        option,
        page,
        rowsPerPage,
        setSimpleSearch,
        tableId,
        searchId,
        isAllChecked,
        thead,
        tbody,
        cancelModal,
        setCancelModal,
        detailsModal,
        setDetailsModal,
        invitationLinkModal,
        setInvitationLinkModal,
        itemSelected,
        setItemSelected,
        cancelFn,
        pagination,
        handleCheckboxChange,
        handleDeleteAll,
        getActionsButtons,
    };
};
